import { inject, injectable } from 'inversify';
import { IModeService, ClientTypes, IResourceContextKey } from '@workbench-stack/core';
import { ClientTypes as FileTypes, IClientFileService } from '@files-stack/core';
import { URI } from '@vscode-alt/monaco-editor/esm/vs/base/common/uri';
import { basename, extname } from '@vscode-alt/monaco-editor/esm/vs/base/common/resources';
import { RawContextKey, IContextKeyService, IContextKey } from '@vscode-alt/monaco-editor/esm/vs/platform/contextkey/common/contextkey';
import { withNullAsUndefined } from '@vscode-alt/monaco-editor/esm/vs/base/common/types';

@injectable()
export class ResourceContextKey {

    public static Scheme = new RawContextKey<string>('resourceScheme', undefined);
    public static Filename = new RawContextKey<string>('resourceFilename', undefined);
    public static LangId = new RawContextKey<string>('resourceLangId', undefined);
    public static Resource = new RawContextKey<URI>('resource', undefined);
    public static Extension = new RawContextKey<string>('resourceExtname', undefined);
    public static HasResource = new RawContextKey<boolean>('resourceSet', false);
    public static IsFileSystemResource = new RawContextKey<boolean>('isFileSystemResource', false);

    private readonly _resourceKey: IContextKey<URI | null>;
    private readonly _schemeKey: IContextKey<string | null>;
    private readonly _filenameKey: IContextKey<string | null>;
    private readonly _langIdKey: IContextKey<string | null>;
    private readonly _extensionKey: IContextKey<string | null>;
    private readonly _hasResource: IContextKey<boolean>;
    private readonly _isFileSystemResource: IContextKey<boolean>;

    constructor(
        @inject(ClientTypes.IContextKeyService)
        contextKeyService: IContextKeyService,
        @inject(ClientTypes.IModeService)
        private _modeService: IModeService,
        @inject(FileTypes.IFileService)
        private _fileService: IClientFileService,
    ) {

        this._schemeKey = ResourceContextKey.Scheme.bindTo(contextKeyService);
        this._filenameKey = ResourceContextKey.Filename.bindTo(contextKeyService);
        this._langIdKey = ResourceContextKey.LangId.bindTo(contextKeyService);
        this._resourceKey = ResourceContextKey.Resource.bindTo(contextKeyService);
        this._extensionKey = ResourceContextKey.Extension.bindTo(contextKeyService);
        this._hasResource = ResourceContextKey.HasResource.bindTo(contextKeyService);
        this._isFileSystemResource = ResourceContextKey.IsFileSystemResource.bindTo(contextKeyService);
    }


    public set(resource: URI | null) {
        const value = resource ? URI.revive(resource) : null;
        if (!ResourceContextKey._uriEquals(this.get(), value)) {
            this._resourceKey.set(value);
            this._schemeKey.set(value ? value.scheme : null);
            this._filenameKey.set(value ? basename(value) : null);
            this._langIdKey.set(value ? this._modeService.getModeIdByFilepathOrFirstLine(value) : null);
            this._extensionKey.set(value ? extname(value) : null);
            this._hasResource.set(!!value);
            const x = this._fileService.canHandleResource(value);
            this._isFileSystemResource.set(x as boolean);
        }
    }

    public reset(): void {
        this._schemeKey.reset();
        this._langIdKey.reset();
        this._resourceKey.reset();
        this._langIdKey.reset();
        this._extensionKey.reset();
        this._hasResource.reset();
        this._isFileSystemResource.reset();
    }

    public get(): URI | undefined {
        return withNullAsUndefined(this._resourceKey.get());
    }

    private static _uriEquals(a: URI | undefined | null, b: URI | undefined | null): boolean {
        if (a === b) {
            return true;
        }
        if (!a || !b) {
            return false;
        }
        return a.scheme === b.scheme // checks for not equals (fail fast)
            && a.authority === b.authority
            && a.path === b.path
            && a.query === b.query
            && a.fragment === b.fragment
            && a.toString() === b.toString(); // for equal we use the normalized toString-form
    }

}
